import type { FragmentType } from 'src/gql';
import React from 'react';
import { TableCell, TableRow } from '@savvy-kit/components/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { Info } from 'lucide-react';
import { DateTime } from 'luxon';
import numeral from 'numeral';
import ColorCodedPerformanceTypography from 'src/components/performance-report/views/color-coded-return';
import { graphql, unmaskFragment } from 'src/gql';
import { performanceFromQuickDate } from 'src/utils/perf-reporting';
import { renderSleeveIcon } from 'src/utils/portfolio-item-display';

import type { PerformanceTimePeriodName } from '@savvy/constants/generated/gql-enums';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';

export const NetWorthAccountSleeveRowFragment = graphql(/* GraphQL */ `
  fragment NetWorthAccountSleeveRow on AccountSleeve {
    id
    displayName
    displayBalance {
      amount
    }
    lastUpdatedAt
    performance {
      timePeriod
      twr
    }
  }
`);

type Props = {
  readonly accountSleeveFrag: FragmentType<typeof NetWorthAccountSleeveRowFragment>;
  readonly isDisconnectedLinkedItem: boolean;
  readonly quickDateForPerformance?: PerformanceTimePeriodName;
  /**
   * Whether the parent table is rendering a performance column. The cell has to be
   * emitted whenever the column exists, even for a sleeve with no performance data,
   * or this row ends up a cell short and its columns stop lining up with the others.
   */
  readonly showPerformanceColumn: boolean;
};

export function NetWorthAccountSleeveRow({
  accountSleeveFrag,
  isDisconnectedLinkedItem,
  quickDateForPerformance,
  showPerformanceColumn,
}: Props) {
  const isSmallScreen = useIsSmallScreen();
  const sleeve = unmaskFragment(NetWorthAccountSleeveRowFragment, accountSleeveFrag);

  const renderName = () => {
    return <Text color="dark">{sleeve.displayName}</Text>;
  };

  return (
    <TableRow key={sleeve.id} className="border-b-0 hover:bg-transparent">
      <TableCell className={cn('w-[35%] align-middle', isSmallScreen && 'w-auto px-0 align-top')}>
        <div className="flex flex-row gap-4 pl-16">
          <div className="flex items-center">
            <div className="flex flex-row items-end pr-4">{renderSleeveIcon()}</div>
            <div className="flex flex-col gap-0 items-center">{renderName()}</div>
          </div>
        </div>
      </TableCell>
      <TableCell className="w-[14%]">—</TableCell>
      <TableCell className={cn('w-[14%] text-left align-middle', isSmallScreen && 'w-auto px-1 text-right align-top')}>
        <div className="flex flex-col gap-0 items-end">
          <div className={cn('flex', isSmallScreen ? 'flex-col gap-0' : 'flex-row gap-1')}>
            <Text
              color={(() => {
                if (isDisconnectedLinkedItem) {
                  return 'secondary';
                }

                if (isSmallScreen) {
                  return 'dark';
                }

                return 'default';
              })()}
              variant={isSmallScreen ? 'body-sm' : undefined}
            >
              {numeral(sleeve.displayBalance.amount).format('$0,0.00')}
            </Text>
            {isDisconnectedLinkedItem && !isSmallScreen && (
              <Tooltip>
                <TooltipTrigger render={<Info className="text-secondary" />} />
                <TooltipContent>{`Last updated ${DateTime.fromISO(
                  sleeve.lastUpdatedAt,
                ).toRelative()}. Plaid has stopped sending us information about this account. Reconnect to keep it up to date.`}</TooltipContent>
              </Tooltip>
            )}
            {isSmallScreen && (
              <Text color="secondary" variant="caption">
                {DateTime.fromISO(sleeve.lastUpdatedAt).toRelative()}
              </Text>
            )}
          </div>
        </div>
      </TableCell>
      {showPerformanceColumn && (
        <TableCell className="w-[14%] text-right">
          {sleeve.performance && quickDateForPerformance && (
            <ColorCodedPerformanceTypography
              performance={parseFloat(
                performanceFromQuickDate({
                  performanceOverTime: sleeve.performance,
                  quickDate: quickDateForPerformance,
                })?.twr ?? '',
              )}
            />
          )}
        </TableCell>
      )}
      {!isSmallScreen && (
        <TableCell className="w-[23%] text-right">
          <div className="flex flex-row items-center justify-end">
            <Text variant="body-sm" color="secondary">
              {DateTime.fromISO(sleeve.lastUpdatedAt).toRelative()}
            </Text>
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}
